import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Mail,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  ChevronLeft,
  ChevronRight,
  Paperclip,
  FileText,
  Eye,
  ExternalLink
} from 'lucide-react';
import { workAuthService } from '@/services/workAuthService';
import { format } from 'date-fns';

const PAGE_SIZE = 20;

interface EmailDocument {
  id: string;
  fileName: string;
  filePath?: string;
  fileSize: number;
  documentType: string;
  reviewStatus: string;
  reviewNotes?: string | null;
  submittedBy?: string;
  reviewedBy?: string | null;
  submittedAt: string;
  reviewedAt?: string | null;
  workAuthId?: string;
}

interface IncomingEmail {
  id: string;
  messageId: string;
  subject: string;
  from: string;
  to: string[];
  body: string;
  htmlBody: string | null;
  receivedAt: string;
  processed: boolean;
  processedAt: string | null;
  error: string | null;
  relatedWorkAuthId: string | null;
  createdAt: string;
  documents?: EmailDocument[];
  workAuthorization?: {
    id: string;
    employee: {
      firstName: string;
      lastName: string;
      email: string;
    };
  } | null;
}

// Helper to decode HTML entities like &lt; &gt; &amp; etc.
const decodeHtmlEntities = (text: string): string => {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};

const IncomingEmailsPage: React.FC = () => {
  const navigate = useNavigate();
  const [emails, setEmails] = useState<IncomingEmail[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState<IncomingEmail | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    totalPages: 0
  });

  const fetchEmails = async (page: number = 1) => {
    setLoading(true);
    try {
      const response = await workAuthService.getIncomingEmails(page, PAGE_SIZE);
      setEmails(response.data);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Failed to load emails:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmails();
  }, []);

  const handleViewEmail = (email: IncomingEmail) => {
    setSelectedEmail(email);
    setDetailOpen(true);
  };

  const getStatusBadge = (email: IncomingEmail) => {
    if (email.error) {
      return (
        <Badge variant="destructive" className="flex items-center gap-1">
          <XCircle className="h-3 w-3" />
          Error
        </Badge>
      );
    }
    if (email.processed) {
      return (
        <Badge className="bg-green-600 hover:bg-green-700 text-white text-xs">
          <CheckCircle className="h-3 w-3 mr-1" />
          Processed
        </Badge>
      );
    }
    return (
      <Badge variant="secondary" className="flex items-center gap-1">
        <Clock className="h-3 w-3" />
        Pending
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy h:mm a');
    } catch {
      return dateString;
    }
  };

  const hasHtmlBody = (email: IncomingEmail) => {
    return email.htmlBody && email.htmlBody.trim().length > 0;
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Incoming Emails</h1>
          <p className="text-muted-foreground mt-1">
            View all emails received at workauth@mmthrift.com
          </p>
        </div>
        <Button onClick={() => fetchEmails(pagination.page)} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Email List */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              {pagination.total} Email{pagination.total !== 1 ? 's' : ''}
            </CardTitle>
            <div className="text-sm text-muted-foreground">
              Page {pagination.page} of {pagination.totalPages || 1}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : emails.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No emails found
            </div>
          ) : (
            <>
              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b">
                    <tr className="text-left text-sm text-muted-foreground">
                      <th className="pb-3 font-medium">From</th>
                      <th className="pb-3 font-medium">Subject</th>
                      <th className="pb-3 font-medium">Linked Employee</th>
                      <th className="pb-3 font-medium">Status</th>
                      <th className="pb-3 font-medium">Received</th>
                      <th className="pb-3 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emails.map((email) => (
                      <tr
                        key={email.id}
                        className="border-b hover:bg-muted/50 cursor-pointer"
                        onClick={() => handleViewEmail(email)}
                      >
                        <td className="py-4">
                          <p className="font-medium text-sm">{email.from}</p>
                        </td>
                        <td className="py-4">
                          <div className="flex items-center gap-2">
                            <p className="text-sm max-w-[300px] truncate">{email.subject}</p>
                            {email.documents && email.documents.length > 0 && (
                              <Badge variant="outline" className="flex items-center gap-1">
                                <Paperclip className="h-3 w-3" />
                                {email.documents.length}
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="py-4">
                          {email.workAuthorization ? (
                            <Button
                              variant="link"
                              className="p-0 h-auto text-sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/hr/work-auth/${email.workAuthorization!.id}`);
                              }}
                            >
                              {email.workAuthorization.employee.firstName}{' '}
                              {email.workAuthorization.employee.lastName}
                              <ExternalLink className="h-3 w-3 ml-1" />
                            </Button>
                          ) : (
                            <span className="text-sm text-muted-foreground"></span>
                          )}
                        </td>
                        <td className="py-4">{getStatusBadge(email)}</td>
                        <td className="py-4 text-sm text-muted-foreground">
                          {formatDate(email.receivedAt)}
                        </td>
                        <td className="py-4">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewEmail(email);
                            }}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex justify-between items-center mt-6">
                <div className="text-sm text-muted-foreground">
                  Showing {((pagination.page - 1) * PAGE_SIZE) + 1} to{' '}
                  {Math.min(pagination.page * PAGE_SIZE, pagination.total)} of {pagination.total}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page === 1}
                    onClick={() => fetchEmails(pagination.page - 1)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page === pagination.totalPages}
                    onClick={() => fetchEmails(pagination.page + 1)}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Email Detail Modal */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              {selectedEmail?.subject}
            </DialogTitle>
            <DialogDescription>
              From: {selectedEmail?.from} " Received: {selectedEmail && formatDate(selectedEmail.receivedAt)}
            </DialogDescription>
          </DialogHeader>

          {selectedEmail && (
            <div className="space-y-4">
              {/* Status and metadata */}
              <div className="flex items-center gap-4 text-sm">
                {getStatusBadge(selectedEmail)}
                {selectedEmail.workAuthorization && (
                  <Button
                    variant="link"
                    className="p-0 h-auto text-sm"
                    onClick={() => {
                      setDetailOpen(false);
                      navigate(`/hr/work-auth/${selectedEmail.workAuthorization!.id}`);
                    }}
                  >
                    View {selectedEmail.workAuthorization.employee.firstName}{' '}
                    {selectedEmail.workAuthorization.employee.lastName}'s Work Auth
                    <ExternalLink className="h-3 w-3 ml-1" />
                  </Button>
                )}
              </div>

              {/* Error message if any */}
              {selectedEmail.error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800">
                  <strong>Error:</strong> {selectedEmail.error}
                </div>
              )}

              {/* Attachments */}
              {selectedEmail.documents && selectedEmail.documents.length > 0 && (
                <div className="space-y-2">
                  <div className="text-sm font-medium flex items-center gap-2">
                    <Paperclip className="h-4 w-4" />
                    Attachments ({selectedEmail.documents.length})
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedEmail.documents.map((doc) => (
                      <Badge
                        key={doc.id}
                        variant="outline"
                        className="flex items-center gap-2 py-1.5 px-3"
                      >
                        <FileText className="h-3 w-3" />
                        {doc.fileName}
                        <span className="text-muted-foreground">
                          ({(doc.fileSize / 1024).toFixed(1)} KB)
                        </span>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Email body */}
              {hasHtmlBody(selectedEmail) ? (
                <Tabs defaultValue="formatted" className="w-full">
                  <TabsList>
                    <TabsTrigger value="formatted">Formatted</TabsTrigger>
                    <TabsTrigger value="plain">Plain Text</TabsTrigger>
                  </TabsList>
                  <TabsContent value="formatted">
                    <ScrollArea className="h-[300px] rounded-md border p-4">
                      <div
                        className="prose prose-sm max-w-none dark:prose-invert"
                        dangerouslySetInnerHTML={{
                          __html: decodeHtmlEntities(selectedEmail.htmlBody || '')
                        }}
                      />
                    </ScrollArea>
                  </TabsContent>
                  <TabsContent value="plain">
                    <ScrollArea className="h-[300px] rounded-md border p-4">
                      <pre className="text-sm whitespace-pre-wrap font-sans">
                        {decodeHtmlEntities(selectedEmail.body)}
                      </pre>
                    </ScrollArea>
                  </TabsContent>
                </Tabs>
              ) : (
                <ScrollArea className="h-[300px] rounded-md border p-4">
                  <pre className="text-sm whitespace-pre-wrap font-sans">
                    {decodeHtmlEntities(selectedEmail.body)}
                  </pre>
                </ScrollArea>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IncomingEmailsPage;
